import { createSignal, For, type JSX, onCleanup, onMount, Show } from "solid-js";
import clsx from "~/util/html/style";
import { ButtonA } from "~/common/button";
import { Icon } from "~/icon";
import { formatBytes } from "~/util/format";
import { filesFromClipboard, pasteBelongsToTextField } from "~/util/image";
import { PasteInstruction } from "~/common/paste_instruction";

// Presentational drag-and-drop file picker. Collects File[] and hands newly added files to the
// caller via `onFiles` — no upload logic; the caller owns the file list and how it's submitted.
export function FileDropzone(props: {
  /* id for the hidden file input, so a field label's `for` opens the picker */
  id?: string;
  accept?: string;
  multiple?: boolean;
  /** Refuses clicks, keys, and drops — for a form mid-submit, when a new file could
   *  land only to be wiped with the staged list. */
  disabled?: boolean;
  /** Claims paste for the whole screen. Only for a dropzone inside a modal dialog, where
   *  `showModal()` makes everything else inert. On an in-page dropzone this would swallow
   *  pastes meant for other fields. */
  pasteAnywhere?: boolean;
  /** Chosen, among several in a modal dialog, for the next paste anywhere on the screen: ringed, and
   *  saying what to press in place of the default body. */
  chosenForPaste?: boolean;
  /** One row instead of a target with room to drop into: for a page that already holds files,
   *  where adding another is a control rather than the thing the page is about. */
  compact?: boolean;
  /** A control on the trailing edge of the box, inside the dashed border. The box is this
   *  component's, so anything that sits on it is handed here rather than laid out beside it. */
  trailing?: JSX.Element;
  onFiles: (files: File[]) => void;
  /** What a click or Enter does instead of opening the file picker, for a box whose main action is
   *  something else; dropped and pasted files still arrive through `onFiles`. */
  onActivate?: () => void;
  class?: string;
  children?: JSX.Element;
}) {
  const [dragging, setDragging] = createSignal(false);
  let input!: HTMLInputElement;
  const activate = () => (props.onActivate ? props.onActivate() : input.click());

  const emit = (list: FileList | File[] | null) => {
    if (props.disabled) return;
    const files = Array.from(list ?? []);
    if (files.length) props.onFiles(files);
  };

  // Like drops, pasted files skip `accept` — the caller validates. This dropzone owns no text
  // field, so under `pasteAnywhere` it steps aside for one it may be sitting next to.
  const acceptPaste = (e: ClipboardEvent) => {
    if (pasteBelongsToTextField(e)) return;

    const pasted = filesFromClipboard(e);
    if (pasted.length === 0) return;
    e.preventDefault();
    emit(pasted);
  };

  onMount(() => {
    // Checked inside the handler, not around this listener: reading the prop at mount would
    // freeze it at its first value.
    const onDocumentPaste = (e: ClipboardEvent) => {
      // Already taken by a focused dropzone's own handler.
      if (e.defaultPrevented) return;
      if (props.pasteAnywhere || props.chosenForPaste) acceptPaste(e);
    };
    document.addEventListener("paste", onDocumentPaste);
    onCleanup(() => document.removeEventListener("paste", onDocumentPaste));
  });

  return (
    <div
      role="button"
      tabindex="0"
      aria-disabled={props.disabled}
      onClick={() => {
        if (!props.disabled) activate();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          if (!props.disabled) activate();
        }
      }}
      onDragOver={(e) => {
        // preventDefault even when disabled, so the browser never opens the file itself.
        e.preventDefault();
        if (!props.disabled) setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        emit(e.dataTransfer?.files ?? null);
      }}
      // Fires only while the dropzone holds focus, so it can't steal a paste meant for another
      // field on the page. Under `pasteAnywhere` the event also bubbles to the document
      // listener, so stand down there or the file lands twice.
      onPaste={(e) => {
        if (!props.pasteAnywhere && !props.chosenForPaste) acceptPaste(e);
      }}
      class={clsx(
        "flex rounded-lg border-2 border-dashed text-sm transition-colors",
        props.compact
          ? "min-h-input-lg items-center justify-between gap-3 px-3 py-1 text-left"
          : "flex-col items-center justify-center gap-1 px-4 py-8 text-center",
        props.disabled
          ? "cursor-not-allowed border-brand-subtle opacity-60"
          : dragging()
            ? "cursor-pointer border-brand-primary bg-brand-light"
            : "cursor-pointer border-brand-subtle hover:border-brand-primary/60",
        props.chosenForPaste && "ring-2 ring-brand-primary ring-offset-2",
        props.class,
      )}
    >
      <input
        ref={input}
        id={props.id}
        type="file"
        accept={props.accept}
        multiple={props.multiple}
        disabled={props.disabled}
        class="hidden"
        onChange={(e) => {
          emit(e.currentTarget.files);
          e.currentTarget.value = "";
        }}
      />
      {props.children ?? (
        <Show
          when={!props.chosenForPaste}
          fallback={
            <span class="text-typography-secondary">
              <PasteInstruction />
            </span>
          }
        >
          <span class="flex min-w-0 items-center gap-2 text-typography-secondary">
            {/* The row is a control, and an upload mark is what says so at a glance. */}
            <Show when={props.compact}>
              <Icon name="upload" size="xs" />
            </Show>
            {/* One line in the compact row, which has a trailing control to make room for; free to
              wrap in the tall box, which has nothing beside it. */}
            <span class={props.compact ? "truncate" : undefined}>
              Drag {props.multiple ? "files" : "file"} here, or{" "}
              <span class="font-medium text-brand-primary">browse</span>
            </span>
          </span>
        </Show>
      )}

      <Show when={props.trailing}>
        {/* The whole box opens the file picker, on click and on Enter alike, so a control sitting
            on it has to keep its own. */}
        <div
          class="shrink-0"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          {props.trailing}
        </div>
      </Show>
    </div>
  );
}

export type ExistingFile = { filename: string; url: string };

// Lists already-stored files (a download link each), with an optional remove control — the
// "current value" companion to SelectedFiles' newly-picked ones.
export function ExistingFiles(props: {
  files: ExistingFile[];
  onRemove?: (index: number) => void;
}) {
  return (
    <ul class="flex flex-col gap-1.5">
      <For each={props.files}>
        {(file, index) => (
          <li class="flex items-center justify-between gap-3 rounded-lg border border-brand-subtle px-3 py-2 text-sm">
            <ButtonA href={file.url} destination="new-tab" variant="link" class="truncate">
              {file.filename}
            </ButtonA>
            <Show when={props.onRemove} keyed>
              {(onRemove) => (
                <button
                  type="button"
                  onClick={() => onRemove(index())}
                  class="text-typography-secondary transition-colors hover:text-typography-primary"
                  aria-label={`Remove ${file.filename}`}
                >
                  <Icon name="x" size="xs" />
                </button>
              )}
            </Show>
          </li>
        )}
      </For>
    </ul>
  );
}

// Lists files collected by FileDropzone, each with its size and a remove control.
export function SelectedFiles(props: { files: File[]; onRemove: (index: number) => void }) {
  return (
    <ul class="flex flex-col gap-1.5">
      <For each={props.files}>
        {(file, index) => (
          <li class="flex items-center justify-between gap-3 rounded-lg border border-brand-subtle px-3 py-2 text-sm">
            <span class="truncate text-typography-primary">{file.name}</span>
            <div class="flex shrink-0 items-center gap-2">
              <span class="text-xs text-typography-secondary">{formatBytes(file.size)}</span>
              <button
                type="button"
                onClick={() => props.onRemove(index())}
                class="text-typography-secondary transition-colors hover:text-typography-primary"
                aria-label={`Remove ${file.name}`}
              >
                <Icon name="x" size="xs" />
              </button>
            </div>
          </li>
        )}
      </For>
    </ul>
  );
}
